import React, { useEffect, useMemo, useRef, useState } from "react";
import buttonStyles from "../buttons/Button.module.css";
import styles from "./DropdownSelectPill.module.css";
import downIcon from "../../assets/icons/down.svg";

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

const VARIANTS = new Set(["primary", "secondary", "special"]);
const TONES = new Set(["blue", "green", "red", "neutral"]);
const SIZES = new Set(["md", "lg"]);

function toCssSize(v) {
  if (v == null) return undefined;
  if (typeof v === "number") return `${v}px`;
  return String(v);
}

function radiusFromHeight(height) {
  if (height == null) return undefined;
  const n =
    typeof height === "number"
      ? height
      : Number(String(height).replace("px", ""));
  if (!Number.isFinite(n)) return undefined;
  return `${Math.round(n / 2)}px`;
}

function normalizeItems({ items, options }) {
  if (Array.isArray(items) && items.length) return items;

  if (Array.isArray(options)) {
    return options.map((o) => ({
      id: o.value,
      name: o.label,
      color: o.color,
      raw: o,
    }));
  }

  return [];
}

const DropdownSelectPill = React.forwardRef(function DropdownSelectPill(
  {
    label,
    placeholder = "Seleccionar...",
    options,
    value,
    onChange,
    error = "",
    disabled = false,

    title,
    items,
    selectedItem,
    setSelectedItem,
    isEditing,
    setIsEditing,

    className,
    style,
    labelStyle,

    variant = "primary",
    tone = "blue",

    size,
    width,
    height,
    fullWidth = false,

    iconLeft = null,
    type = "button",
    chevronSize,
    dropUp = false,
    menuMaxHeight,
    menuMinWidth,
    ...props
  },
  ref,
) {
  const rootRef = useRef(null);
  const [openInner, setOpenInner] = useState(false);

  const open = typeof isEditing === "boolean" ? isEditing : openInner;
  const setOpen =
    typeof setIsEditing === "function" ? setIsEditing : setOpenInner;

  const normalizedItems = useMemo(
    () => normalizeItems({ items, options }),
    [items, options],
  );

  const selected =
    selectedItem ??
    normalizedItems.find((it) => String(it.id) === String(value)) ??
    null;

  const resolvedLabel = label ?? title ?? "";

  const resolvedVariant = VARIANTS.has(variant) ? variant : "primary";
  const resolvedTone = TONES.has(tone) ? tone : "blue";

  const inferredSize = resolvedVariant === "special" ? "lg" : "md";
  const resolvedSize = SIZES.has(size) ? size : inferredSize;

  const rootStyle = {
    width: fullWidth ? "100%" : toCssSize(width),
    height: toCssSize(height),
    borderRadius: radiusFromHeight(height),
    ...style,
  };

  useEffect(() => {
    function onDocMouseDown(e) {
      if (!open) return;
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      setOpen(false);
    }

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, setOpen]);

  function handleToggle() {
    if (disabled) return;
    setOpen(!open);
  }

  function handlePick(item) {
    if (disabled) return;

    setSelectedItem?.(item);
    onChange?.(item.id);

    setOpen(false);
  }

  return (
    <div
      className={cx(styles.wrapper, fullWidth ? buttonStyles.fullWidth : "")}
      ref={rootRef}
    >
      {resolvedLabel ? (
        <div className={styles.fieldLabel}>{resolvedLabel}</div>
      ) : null}

      <button
        {...props}
        ref={ref}
        type={type}
        disabled={disabled}
        onClick={handleToggle}
        className={cx(
          buttonStyles.btn,
          buttonStyles[`variant_${resolvedVariant}`],
          buttonStyles[`tone_${resolvedTone}`],
          buttonStyles[`size_${resolvedSize}`],
          fullWidth ? buttonStyles.fullWidth : "",
          disabled ? buttonStyles.disabled : "",
          styles.trigger,
          open ? styles.open : "",
          error ? styles.error : "",
          className,
        )}
        style={rootStyle}
        aria-expanded={open}
      >
        {iconLeft ? (
          <span className={buttonStyles.iconLeft}>{iconLeft}</span>
        ) : null}

        <span
          className={cx(buttonStyles.label, styles.triggerLabel)}
          style={labelStyle}
        >
          {selected ? selected.name : placeholder}
        </span>

        <span className={styles.chevronWrap}>
          <img
            src={downIcon}
            alt=""
            className={cx(styles.chevron, open ? styles.chevronUp : "")}
            style={chevronSize != null ? { width: chevronSize, height: chevronSize } : undefined}
          />
        </span>
      </button>

      {open ? (
        <div
          className={cx(styles.menu, dropUp ? styles.menuUp : "")}
          style={{
            ...(menuMaxHeight != null && { maxHeight: menuMaxHeight, overflowY: "auto" }),
            ...(menuMinWidth != null && { minWidth: menuMinWidth }),
          }}
        >
          {normalizedItems.map((it) => (
            <button
              type="button"
              key={String(it.id)}
              className={styles.option}
              onClick={() => handlePick(it)}
            >
              {it.name}
            </button>
          ))}
        </div>
      ) : null}

      {error ? <div className={styles.errorText}>{error}</div> : null}
    </div>
  );
});

export default DropdownSelectPill;
